import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { ArrowDownIcon } from '@/components/icons/ArrowDownIcon';

export type ButtonsDropdownOption = {
  value: string;
  label: string;
  onClick: () => void;
  disabled?: boolean;
};

type ButtonsDropdownProps = {
  label: string;
  disabled?: boolean;
  options: ButtonsDropdownOption[];
  className?: string;
};

export default function ButtonsDropdown({ label, disabled = false, options, className }: ButtonsDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isOpen) return;
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleOptionClick = (option: ButtonsDropdownOption) => {
    if (option.disabled) return;
    setIsOpen(false);
    option.onClick();
  };

  return (
    <div ref={dropdownRef} className={clsx('relative self-start', className)}>
      <button
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled}
        className="flex items-center h-[40px] rounded-full border border-gray-200 bg-white cursor-pointer hover:brightness-95 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className="px-4 text-sm font-semibold text-[#113D7B] whitespace-nowrap">{label}</span>
        <div className="w-px h-5 bg-gray-200" />
        <span className="px-2 flex items-center justify-center">
          <ArrowDownIcon isOpen={isOpen} />
        </span>
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 mt-1 w-48 bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden z-[1000]">
          {options.map((option, index) => (
            <button
              key={option.value}
              className={clsx(
                'w-full text-left px-4 py-3 text-sm font-medium font-avenir',
                index < options.length - 1 && 'border-b border-gray-100',
                option.disabled
                  ? 'text-gray-300 cursor-not-allowed'
                  : 'text-slate-700 cursor-pointer hover:bg-slate-50 hover:text-blue-800'
              )}
              disabled={option.disabled}
              onClick={() => handleOptionClick(option)}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
